// @flow
import { createActions, handleActions } from 'redux-actions';
import { destroy as destroyForm } from 'redux-form';
import Notifications from 'react-notification-system-redux';

/** Maps API validation field names to form field names per section */
const API_TO_FORM_FIELD_MAP: { [string]: string } = {
    propertyReference: 'reference',
    propertyName: 'name',
    propertyAddress: 'address',
    propertyTown: 'townCity',
    propertyPostCode: 'postcode',
    propertyRegionId: 'regionId',
    propertyCountyId: 'countyId',
    propertyBoroughId: 'boroughId',
    propertyBusinessEstateId: 'businessEstateId',
    propertyStationId: 'nearestStationId',
    propertyLat: 'geolocation',
    propertyLong: 'geolocation',
    propertyAvailableFrom: 'availableFrom',
    propertyStatus: 'status',
    propertyDescription: 'description',
    propertyLocationDescription: 'locationDescription',
    propertyUserPlanning: 'userPlanning',
    propertyOtherInformation: 'otherInformation',
    propertyServiceCharge: 'serviceCharge',
    propertyMaintenance: 'maintenance',
    propertyTypes: 'propertyTypeIds',
    propertyKeyDetails1: 'keyDetails1',
    propertyKeyDetails2: 'keyDetails2',
    propertyKeyDetails3: 'keyDetails3',
    propertyKeyDetails4: 'keyDetails4',
    propertyKeyDetails5: 'keyDetails5',
    propertyPropertyAgreementTypeId: 'agreementTypeId',
    propertyPropertyAgreementDescription: 'agreementTypeDescription',
    propertySolutionsRPI: 'solutionsRPI',
    propertyRent: 'rent',
    propertyRentText: 'rentText',
    propertySize: 'size',
    propertyRateableValue: 'rateableValue',
    propertyPropertyInsuranceId: 'insuranceTypeId',
    propertyPropertyInsuranceOther: 'insuranceTypeOther',
    propertyPropertyInsuranceAmount: 'insuranceAmount',
    propertyHasRepairGuarantee: 'repairGuarantee',
    propertyLocalAuthority: 'localAuthority',
    propertyDeposit: 'deposit',
    propertyCosts: 'costs',
    propertyBusinessRates: 'businessRates',
    propertyPromoType: 'promotionType',
    propertyPromoAfterPrice: 'promotionAfterPrice',
    propertyPromoRentFreeMonths: 'rentFreeMonths',
    propertyContacts: 'contacts',
    propertyContactMoreInfo: 'showContactMoreInfo',
    propertyIsContactRequestByRegion: 'contactRequestByRegion',
    propertyContactRequestRegionId: 'contactRequestRegionId',
};

const getValidationMessages = (data: any): string[] => {
    const messages = [];
    const validation = data.validation || data.errors;
    if (validation && typeof validation === 'object') {
        Object.values(validation).forEach(value => {
            if (Array.isArray(value)) {
                value.forEach(msg => {
                    if (typeof msg === 'string') messages.push(msg);
                });
            }
        });
    }
    return messages;
};

const getErrorMessageFromResponse = (err: any): ?string => {
    if (!err || !err.response) return null;
    const { data, status } = err.response;
    if (status === 400 && data) {
        const validationMessages = getValidationMessages(data);
        if (validationMessages.length > 0) {
            return validationMessages.join(' ');
        }
        if (typeof data.message === 'string') return data.message;
        if (typeof data.error === 'string') return data.error;
        if (data.errors && typeof data.errors === 'object') {
            const messages = Object.values(data.errors).reduce(
                (acc, val) => acc.concat(Array.isArray(val) ? val : [val]),
                []
            );
            if (messages.length > 0) {
                return messages.filter(Boolean).join('. ');
            }
        }
    }
    return null;
};

const extractValidationFromData = (data: any): ?{ [string]: string } => {
    const validation = data.validation || data.errors;
    if (!validation || typeof validation !== 'object') return null;

    const formErrors = {};
    Object.entries(validation).forEach(([apiField, messages]) => {
        const formField = API_TO_FORM_FIELD_MAP[apiField] || apiField;
        const message = Array.isArray(messages) ? messages[0] : messages;
        if (typeof message === 'string') {
            formErrors[formField] = formErrors[formField]
                ? `${formErrors[formField]}. ${message}`
                : message;
        }
    });
    return Object.keys(formErrors).length > 0 ? formErrors : null;
};

export const getValidationErrorsForForm = (err: any): ?{ [string]: string } => {
    if (!err || !err.response || !err.response.data) return null;
    return extractValidationFromData(err.response.data);
};

const actions = createActions({
    PROPERTY: {
        DETAIL: {
            FETCHING: propertyReference => ({ propertyReference }),
            LOADED: property => ({ property }),
        },

        ADD: {
            ADDING: undefined,
            ADDED: property => ({ property }),
        },

        UPDATE: {
            UPDATING: undefined,
            UPDATED: property => ({ property }),
            FAILED: undefined,
        },

        REMOVE: {
            PROMPT: ({ propertyId, propertyReference }) => ({
                propertyId,
                propertyReference,
            }),
            ENTERED_REFERENCE_CHANGE: enteredReference => ({
                enteredReference,
            }),
            REMOVING: undefined,
            REMOVED: undefined,
            RESET: undefined,
        },

        DUPLICATE: {
            PROMPT: ({ propertyId, propertyReference }) => ({
                propertyId,
                propertyReference,
            }),
            ENTERED_REFERENCE_CHANGE: enteredReference => ({
                enteredReference,
            }),
            DUPLICATING: undefined,
            DUPLICATED: undefined,
            RESET: undefined,
        },

        EXPORT: {
            PROMPT: ({ searchState }) => ({ searchState }),
            GENERATING: undefined,
            GENERATED: ({ downloadUrl, matches }) => ({ downloadUrl, matches }),
            RESET: undefined,
        },

        TO_LET_BOARD: {
            PROMPT: ({ propertyReference }) => ({ propertyReference }),
            SAVING: undefined,
            SAVED: undefined,
            RESET: undefined,
        },

        AGREEMENT_TYPES: {
            FETCHING: undefined,
            LOADED: agreementTypes => ({ agreementTypes }),
            SAVING: undefined,
            SAVED: () => ({}),
        },

        INSURANCE_TYPES: {
            FETCHING: undefined,
            LOADED: insuranceTypes => ({ insuranceTypes }),
            SAVING: undefined,
            SAVED: () => ({}),
        },

        CHECK_REFERENCE: isValid => ({ isValid }),
    },
});

const initialState = {
    add: {
        error: null,
        adding: false,
    },
    detail: {
        error: null,
        fetching: false,
        property: null,
    },
    update: {
        error: null,
        updating: false,
    },
    remove: {
        propertyId: null,
        propertyReference: null,
        enteredReference: null,
        removing: false,
    },
    duplicate: {
        propertyId: null,
        propertyReference: null,
        enteredReference: null,
        duplicating: false,
    },
    export: {
        searchState: null,
        visible: false,
        generating: false,
        generated: false,
        downloadUrl: null,
        matches: null,
    },
    toLetBoard: {
        propertyReference: null,
        visible: false,
        saving: false,
        saved: false,
    },
    agreementTypes: {
        error: null,
        fetching: false,
        saving: false,
        list: null,
    },
    insuranceTypes: {
        error: null,
        fetching: false,
        saving: false,
        list: null,
    },
};

export default handleActions(
    {
        // DETAIL START
        [actions.property.detail.fetching]: (state, action) => {
            return Object.assign({}, state, {
                detail: Object.assign({}, initialState.detail, {
                    fetching: true,
                }),
            });
        },

        [actions.property.detail.loaded]: {
            next: (state, action) => {
                return Object.assign({}, state, {
                    detail: Object.assign({}, initialState.detail, {
                        property: action.payload.property,
                    }),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    detail: Object.assign({}, initialState.detail, {
                        error: true,
                    }),
                });
            },
        },

        // UPDATE START
        [actions.property.update.updating]: (state, action) => {
            return Object.assign({}, state, {
                update: Object.assign({}, initialState.update, {
                    updating: true,
                }),
            });
        },

        [actions.property.update.updated]: (state, action) => {
            return Object.assign({}, state, {
                detail: Object.assign({}, initialState.detail, {
                    property: action.payload.property,
                }),
                update: Object.assign({}, initialState.update),
            });
        },

        [actions.property.update.failed]: (state, action) => {
            return Object.assign({}, state, {
                update: Object.assign({}, initialState.update, {
                    error: true,
                }),
            });
        },

        // REMOVAL PROMPT START
        [actions.property.remove.prompt]: (state, action) => {
            const { propertyId, propertyReference } = action.payload;

            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove, {
                    propertyId,
                    propertyReference,
                }),
            });
        },

        [actions.property.remove.enteredReferenceChange]: (state, action) => {
            const { enteredReference } = action.payload;

            return Object.assign({}, state, {
                remove: Object.assign({}, state.remove, {
                    enteredReference: enteredReference.toUpperCase(),
                }),
            });
        },

        [actions.property.remove.removing]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, state.remove, {
                    removing: true,
                }),
            });
        },

        [actions.property.remove.removed]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove),
            });
        },

        [actions.property.remove.reset]: (state, action) => {
            return Object.assign({}, state, {
                remove: Object.assign({}, initialState.remove),
            });
        },

        // DUPLICATE PROMPT START
        [actions.property.duplicate.prompt]: (state, action) => {
            const { propertyId, propertyReference } = action.payload;

            return Object.assign({}, state, {
                duplicate: Object.assign({}, initialState.duplicate, {
                    propertyId,
                    propertyReference,
                }),
            });
        },

        [actions.property.duplicate.enteredReferenceChange]: (
            state,
            action
        ) => {
            const { enteredReference } = action.payload;

            return Object.assign({}, state, {
                duplicate: Object.assign({}, state.duplicate, {
                    enteredReference: enteredReference.toUpperCase(),
                }),
            });
        },

        [actions.property.duplicate.duplicating]: (state, action) => {
            return Object.assign({}, state, {
                duplicate: Object.assign({}, state.duplicate, {
                    duplicating: true,
                }),
            });
        },

        [actions.property.duplicate.duplicated]: (state, action) => {
            return Object.assign({}, state, {
                duplicate: Object.assign({}, initialState.duplicate),
            });
        },

        [actions.property.duplicate.reset]: (state, action) => {
            return Object.assign({}, state, {
                duplicate: Object.assign({}, initialState.duplicate),
            });
        },

        // EXPORT PROMPT START
        [actions.property.export.prompt]: (state, action) => {
            const { searchState } = action.payload;

            return Object.assign({}, state, {
                export: Object.assign({}, initialState.export, {
                    searchState,
                    visible: true,
                }),
            });
        },

        [actions.property.export.generating]: (state, action) => {
            return Object.assign({}, state, {
                export: Object.assign({}, state.export, {
                    generating: true,
                    generated: false,
                    downloadUrl: null,
                    matches: null,
                }),
            });
        },

        [actions.property.export.generated]: {
            next: (state, action) => {
                const { downloadUrl, matches } = action.payload;

                return Object.assign({}, state, {
                    export: Object.assign({}, state.export, {
                        generating: false,
                        generated: true,
                        downloadUrl,
                        matches,
                    }),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    export: Object.assign({}, initialState.export, {
                        visible: true,
                        error: true,
                    }),
                });
            },
        },

        [actions.property.export.reset]: (state, action) => {
            return Object.assign({}, state, {
                export: Object.assign({}, initialState.export),
            });
        },

        // TO LET PROMPT START
        [actions.property.toLetBoard.prompt]: (state, action) => {
            const { propertyReference } = action.payload;

            return Object.assign({}, state, {
                toLetBoard: Object.assign({}, initialState.toLetBoard, {
                    propertyReference,
                    visible: true,
                }),
            });
        },

        [actions.property.toLetBoard.saving]: (state, action) => {
            return Object.assign({}, state, {
                toLetBoard: Object.assign({}, state.toLetBoard, {
                    saving: true,
                    saved: false,
                }),
            });
        },

        [actions.property.toLetBoard.saved]: {
            next: (state, action) => {
                // const { downloadUrl, matches } = action.payload;

                return Object.assign({}, state, {
                    toLetBoard: Object.assign({}, state.toLetBoard, {
                        saving: false,
                        saved: true,
                        visible: false,
                    }),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    toLetBoard: Object.assign({}, initialState.toLetBoard, {
                        visible: true,
                        error: true,
                    }),
                });
            },
        },

        [actions.property.toLetBoard.reset]: (state, action) => {
            return Object.assign({}, state, {
                toLetBoard: Object.assign({}, initialState.toLetBoard),
            });
        },

        // AGREEMENT TYPES START
        [actions.property.agreementTypes.fetching]: (state, action) => {
            return Object.assign({}, state, {
                agreementTypes: Object.assign({}, initialState.agreementTypes, {
                    fetching: true,
                }),
            });
        },

        [actions.property.agreementTypes.loaded]: {
            next: (state, action) => {
                return Object.assign({}, state, {
                    agreementTypes: Object.assign(
                        {},
                        initialState.agreementTypes,
                        {
                            list: action.payload.agreementTypes.reduce(
                                (result, agreementType) => {
                                    result[
                                        agreementType.propertyAgreementTypeId
                                    ] = agreementType;
                                    return result;
                                },
                                {}
                            ),
                        }
                    ),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    agreementTypes: Object.assign(
                        {},
                        initialState.agreementTypes,
                        {
                            error: true,
                        }
                    ),
                });
            },
        },

        [actions.property.agreementTypes.saving]: (state, action) =>
            Object.assign({}, state, {
                agreementTypes: Object.assign({}, state.agreementTypes, {
                    saving: true,
                }),
            }),

        [actions.property.agreementTypes.saved]: {
            next: (state, action) =>
                Object.assign({}, state, {
                    agreementTypes: Object.assign({}, state.agreementTypes, {
                        saving: false,
                    }),
                }),
            throw: (state, action) =>
                Object.assign({}, state, {
                    agreementTypes: Object.assign({}, state.agreementTypes, {
                        error: true,
                        saving: false,
                    }),
                }),
        },

        // INSURANCE TYPES START
        [actions.property.insuranceTypes.fetching]: (state, action) => {
            return Object.assign({}, state, {
                insuranceTypes: Object.assign({}, initialState.insuranceTypes, {
                    fetching: true,
                }),
            });
        },

        [actions.property.insuranceTypes.loaded]: {
            next: (state, action) => {
                return Object.assign({}, state, {
                    insuranceTypes: Object.assign(
                        {},
                        initialState.insuranceTypes,
                        {
                            list: action.payload.insuranceTypes.reduce(
                                (result, insuranceType) => {
                                    result[
                                        insuranceType.propertyInsuranceId
                                    ] = insuranceType;
                                    return result;
                                },
                                {}
                            ),
                        }
                    ),
                });
            },
            throw: (state, action) => {
                return Object.assign({}, state, {
                    insuranceTypes: Object.assign(
                        {},
                        initialState.insuranceTypes,
                        {
                            error: true,
                        }
                    ),
                });
            },
        },

        [actions.property.insuranceTypes.saving]: (state, action) =>
            Object.assign({}, state, {
                insuranceTypes: Object.assign({}, state.insuranceTypes, {
                    saving: true,
                }),
            }),

        [actions.property.insuranceTypes.saved]: {
            next: (state, action) =>
                Object.assign({}, state, {
                    insuranceTypes: Object.assign({}, state.insuranceTypes, {
                        saving: false,
                    }),
                }),
            throw: (state, action) =>
                Object.assign({}, state, {
                    insuranceTypes: Object.assign({}, state.insuranceTypes, {
                        error: true,
                        saving: false,
                    }),
                }),
        },
    },
    initialState
);

export const loadProperty = (propertyReference: string) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.detail.fetching(propertyReference));

        return api
            .get(`/properties/byreference/${propertyReference}`)
            .then(
                response =>
                    dispatch(actions.property.detail.loaded(response.data)),
                err => dispatch(actions.property.detail.loaded(err))
            );
    };
};

export const addProperty = (property: any) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.add.adding());

        return api.post('/properties', property).then(
            response => {
                dispatch(actions.property.add.added(response.data));

                // this is needed to clear the 'dirty' flag and prevent warning that data may be lost
                dispatch(destroyForm('propertyEditLocation'));

                dispatch(
                    Notifications.success({
                        title: 'Property created',
                        message: 'the property has been created.',
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );

                return response.data;
            },
            err => {
                dispatch(actions.property.add.added(err));
                dispatch(
                    Notifications.error({
                        title: 'Creation failed',
                        message:
                            'An error occurred whilst creating this property. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};

// Generates the property brochure PDF on the fly and opens it in a new tab.
// The blank tab is opened synchronously (in this action creator, before the
// async request) so browsers don't treat the later navigation as a popup.
export const generatePropertyBrochure = (propertyReference: string) => {
    const windowTab = window.open();

    return (dispatch: Function, getState: Function, api: Function) => {
        return api
            .get(`/properties/byreference/${propertyReference}/brochure`)
            .then(
                response => {
                    const downloadUrl =
                        response.data && response.data.downloadUrl;

                    if (downloadUrl && windowTab) {
                        windowTab.location.href = downloadUrl;
                        return;
                    }

                    if (windowTab) {
                        windowTab.close();
                    }

                    dispatch(
                        Notifications.error({
                            title: 'Brochure unavailable',
                            message:
                                (response.data &&
                                    (response.data.message ||
                                        response.data.error)) ||
                                'The brochure could not be generated for this property.',
                            position: 'tc',
                            autoDismiss: 5,
                        })
                    );
                },
                () => {
                    if (windowTab) {
                        windowTab.close();
                    }

                    dispatch(
                        Notifications.error({
                            title: 'Brochure generation failed',
                            message:
                                'An error occurred whilst generating the brochure. Please try again.',
                            position: 'tc',
                            autoDismiss: 5,
                        })
                    );
                }
            );
    };
};

export const updateProperty = (
    propertyId: string,
    property: any,
    currentForm: string
) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.update.updating());

        return api.patch(`/properties/${propertyId}`, property).then(
            response => {
                dispatch(actions.property.update.updated(response.data));

                // this is needed to clear the 'dirty' flag and prevent warning that data may be lost
                if (currentForm) {
                    dispatch(destroyForm(currentForm));
                }

                dispatch(
                    Notifications.success({
                        title: 'Property updated',
                        message: 'The provided details have been saved.',
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );

                return response.data;
            },
            err => {
                dispatch(actions.property.update.failed());

                const errorMessage = getErrorMessageFromResponse(err);

                dispatch(
                    Notifications.error({
                        title: 'Update failed',
                        message:
                            errorMessage ||
                            'An error occurred whilst saving this property. Please try again.',
                        position: 'tc',
                        autoDismiss: 0,
                    })
                );

                throw err;
            }
        );
    };
};

export const showDeletePropertyPrompt = (
    propertyId: string,
    propertyReference: string
) => {
    return actions.property.remove.prompt({ propertyId, propertyReference });
};

export const deletePromptEnteredReferenceChange = (value: string) => {
    return actions.property.remove.enteredReferenceChange(value);
};

export const deletePromptReset = () => {
    return actions.property.remove.reset();
};

export const removeProperty = (
    propertyId: string,
    propertyReference: string
) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.remove.removing());

        return api.delete(`/properties/${propertyId}`).then(
            response => {
                dispatch(actions.property.remove.removed());

                dispatch(
                    Notifications.success({
                        title: 'Property deleted',
                        message: `The property ${propertyReference} has been deleted.`,
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );

                // update history to move to next edit screen
                // history.push(nextSection);
            },
            err => {
                dispatch(actions.property.remove.removed(err));
                dispatch(
                    Notifications.error({
                        title: 'Delete failed',
                        message:
                            'An error occurred whilst deleting this property. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};

export const showDuplicatePropertyPrompt = (
    propertyId: string,
    propertyReference: string
) => {
    return actions.property.duplicate.prompt({ propertyId, propertyReference });
};

export const duplicatePromptEnteredReferenceChange = (value: string) => {
    return actions.property.duplicate.enteredReferenceChange(value);
};

export const duplicatePromptReset = () => {
    return actions.property.duplicate.reset();
};

export const duplicateProperty = (
    propertyId: string,
    enteredReference: string
) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.duplicate.duplicating());

        return api
            .post(`/properties/${propertyId}/duplicate`, {
                propertyReference: enteredReference,
            })
            .then(
                response => {
                    dispatch(actions.property.duplicate.duplicated());

                    dispatch(
                        Notifications.success({
                            title: 'Property duplicated',
                            message: `The property has been duplicated successfully.`,
                            position: 'tc',
                            autoDismiss: 3,
                        })
                    );

                    return 'success';
                },
                err => {
                    dispatch(actions.property.duplicate.duplicated(err));

                    dispatch(
                        Notifications.error({
                            title: 'Property duplicated',
                            message: `The property ${enteredReference} already exists.`,
                            position: 'tc',
                            autoDismiss: 3,
                        })
                    );

                    return 'error';
                }
            );
    };
};

export const exportPromptShow = (searchState: any) => {
    return actions.property.export.prompt({ searchState });
};

export const exportGenerate = (contactId: ?string, format: ?string) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        const exportState = getState().property.export;
        const searchState = exportState.searchState || {};
        const filters = {};

        if (contactId && contactId.length > 0) {
            filters.contactId = contactId;
        }

        // check for property size/rent ranges
        if (searchState.range) {
            if (searchState.range.PropertySize) {
                filters.PropertySize = {
                    from: searchState.range.PropertySize.min,
                    to: searchState.range.PropertySize.max,
                };
            }

            if (searchState.range.PropertyRent) {
                filters.PropertyRent = {
                    from: searchState.range.PropertyRent.min,
                    to: searchState.range.PropertyRent.max,
                };
            }
        }

        if (searchState.refinementList) {
            if (
                Array.isArray(searchState.refinementList.PropertyTypes) &&
                searchState.refinementList.PropertyTypes.length > 0
            ) {
                filters.PropertyTypes =
                    searchState.refinementList.PropertyTypes;
            }

            if (
                Array.isArray(
                    searchState.refinementList.PropertyBusinessEstate
                ) &&
                searchState.refinementList.PropertyBusinessEstate.length > 0
            ) {
                filters.PropertyBusinessEstates =
                    searchState.refinementList.PropertyBusinessEstate;
            }

            if (
                Array.isArray(searchState.refinementList.PropertyStatus) &&
                searchState.refinementList.PropertyStatus.length > 0
            ) {
                filters.PropertyStatuses =
                    searchState.refinementList.PropertyStatus;
            }
        }

        const apiParams = Object.assign(
            {},
            {
                query: searchState.query,
                filters,
                format,
            }
        );

        dispatch(actions.property.export.generating());

        return api.post('/properties/export', apiParams).then(
            response =>
                dispatch(
                    dispatch(actions.property.export.generated(response.data))
                ),
            err => {
                dispatch(actions.property.export.generated(err));
                dispatch(
                    Notifications.error({
                        title: 'Export failed',
                        message:
                            'An error occurred whilst generating the export. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};

export const exportPromptReset = () => {
    return actions.property.export.reset();
};

export const toLetBoardShow = (propertyReference: string) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.toLetBoard.prompt({ propertyReference }));
        dispatch(loadProperty(propertyReference));
    };
};

export const toLetBoardSubmit = (data: any) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.toLetBoard.saving());

        return api.post(`/toletboards`, data).then(
            response => {
                dispatch(actions.property.toLetBoard.saved());

                dispatch(
                    Notifications.success({
                        title: 'To Let Board Instruction Created',
                        message: `Your request has been saved and Signs 2000 has been notified.`,
                        position: 'tc',
                        autoDismiss: 3,
                    })
                );
            },
            err => {
                dispatch(actions.property.toLetBoard.saved(err));
                dispatch(
                    Notifications.error({
                        title: 'Submission failed',
                        message:
                            'An error occurred whilst creating the to let board instruction. Please try again.',
                        position: 'tc',
                        autoDismiss: 5,
                    })
                );
                throw err;
            }
        );
    };
};

export const toLetBoardReset = () => {
    return actions.property.toLetBoard.reset();
};

export const loadAgreementTypes = () => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.agreementTypes.fetching());

        return api
            .get('/properties/propertyagreementtypes')
            .then(
                response =>
                    dispatch(
                        actions.property.agreementTypes.loaded(response.data)
                    ),
                err => dispatch(actions.property.agreementTypes.loaded(err))
            );
    };
};

export const updateAgreementTypes = (agreementTypes: any) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.agreementTypes.saving());

        return api
            .post('/properties/propertyagreementtypes/batchupdate', {
                agreementTypes,
            })
            .then(response => {
                dispatch(actions.property.agreementTypes.saved(response.data));
            })
            .catch(err => {
                dispatch(actions.property.agreementTypes.saved(err));
            });
    };
};

export const loadInsuranceTypes = () => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.insuranceTypes.fetching());

        return api
            .get('/properties/propertyinsurances')
            .then(
                response =>
                    dispatch(
                        actions.property.insuranceTypes.loaded(response.data)
                    ),
                err => dispatch(actions.property.insuranceTypes.loaded(err))
            );
    };
};

export const updateInsuranceTypes = (insuranceTypes: any) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        dispatch(actions.property.insuranceTypes.saving());

        return api
            .post('/properties/propertyinsurances/batchupdate', {
                insuranceTypes,
            })
            .then(response => {
                dispatch(actions.property.insuranceTypes.saved(response.data));
            })
            .catch(err => {
                dispatch(actions.property.insuranceTypes.saved(err));
            });
    };
};

export const propertyReferenceIsValid = (propertyReference: string) => {
    return (dispatch: Function, getState: Function, api: Function) => {
        const currentProperty = getState().property.detail.property;
        const matchesCurrent =
            currentProperty &&
            currentProperty.propertyReference &&
            currentProperty.propertyReference.toUpperCase() ===
                propertyReference.toUpperCase();

        return api
            .get(`/properties/byreference/${propertyReference.toUpperCase()}`)
            .then(
                response => {
                    const isValid = matchesCurrent || false;

                    return dispatch(actions.property.checkReference(isValid));
                },
                err => dispatch(actions.property.checkReference(true))
            );
    };
};



// WEBPACK FOOTER //
// ./src/redux/reducers/property/index.js